import React, { useMemo, useState, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppData } from '../contexts/AppDataContext';
import { useLocale } from '../contexts/LocaleContext';
import {
  Building2,
  Shield,
  Eye,
  BarChart3,
  Plus,
  RefreshCw,
} from 'lucide-react';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import { colors } from '../config/colors';
import { Card } from '../components/UI/card';
import { Button } from '../components/UI/button';
import { KpiCard } from '../components/UI/kpi-card';
import { themeClasses } from '../config/themeClasses';
import InteractiveWorldMap from '../components/Maps/InteractiveWorldMap';
import MapShimmer from '../components/Maps/MapShimmer';

const DashboardWatchlistNewsSentiment = lazy(() =>
  import('../components/Dashboard/DashboardWatchlistNewsSentiment')
);
const DashboardRecentAndAlerts = lazy(() => import('./DashboardRecentAndAlerts'));

function SectionShimmer({ height = 192 }) {
  return (
    <div
      className="animate-pulse rounded-md bg-gray-200"
      style={{ height }}
    />
  );
}

const Dashboard = () => {
  const navigate = useNavigate();
  const { t, locale } = useLocale();
  const { companiesStats, watchlist, loading } = useAppData();
  const [mapRefreshTrigger, setMapRefreshTrigger] = useState(0);
  const [mapLoadingDone, setMapLoadingDone] = useState(false);

  const totalRoutes = useMemo(
    () =>
      watchlist.reduce((sum, item) => sum + (item.userCompanyInfo?.routes?.length || 0), 0),
    [watchlist]
  );

  const watchlistCount = useMemo(() => watchlist.length, [watchlist]);

  const highRiskCount = useMemo(
    () => watchlist.filter((item) => (item.priority || '').toLowerCase() === 'high').length,
    [watchlist]
  );

  const overviewDate = useMemo(() => {
    const loc = locale === 'fr' ? 'fr-CA' : 'en-US';
    return new Date().toLocaleDateString(loc, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }, [locale]);

  const riskScoreAvg = useMemo(() => {
    const withScore = (watchlist || []).filter(
      (item) => (item.company?.overallRiskScore ?? item.company?.overall_risk_score) != null
    );
    if (withScore.length === 0) return null;
    const sum = withScore.reduce(
      (s, item) => s + (item.company?.overallRiskScore ?? item.company?.overall_risk_score ?? 0),
      0
    );
    return (sum / withScore.length).toFixed(1);
  }, [watchlist]);

  const mapRiskAnalyses = useMemo(() => {
    const m = {};
    (watchlist || []).forEach((item) => {
      const company = item.company;
      const ticker = item.companyId ?? item.company_id ?? company?.ticker;
      const score = company?.overallRiskScore ?? company?.overall_risk_score;
      if (score != null && score > 0 && ticker) {
        m[ticker] = { overallRiskScore: score };
      }
    });
    return m;
  }, [watchlist]);

  const kpiCards = useMemo(
    () => [
      {
        nameKey: 'totalCompanies',
        value: companiesStats.totalCompanies ?? 0,
        icon: Building2,
        iconColor: colors.primary[500],
        bgColor: colors.primary[50],
        trend: false,
      },
      {
        nameKey: 'highRiskCount',
        value: highRiskCount,
        icon: Shield,
        iconColor: colors.danger[500],
        bgColor: colors.danger[50],
        trend: highRiskCount > 0,
        trendKey: 'trendNewSinceYesterday',
        trendParams: { count: highRiskCount },
        trendPositive: false,
      },
      {
        nameKey: 'watchlistKpi',
        value: watchlistCount,
        icon: Eye,
        iconColor: colors.success[500],
        bgColor: colors.success[50],
        subtitleKey: 'activeMonitoringRoutes',
        subtitleParams: { count: totalRoutes },
      },
      {
        nameKey: 'riskScoreAvg',
        value: riskScoreAvg ?? '—',
        icon: BarChart3,
        iconColor: colors.info[500],
        bgColor: colors.info[50],
        trend: false,
      },
    ],
    [companiesStats.totalCompanies, watchlistCount, highRiskCount, totalRoutes, riskScoreAvg]
  );

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className={`relative overflow-hidden rounded-lg ${themeClasses.appKit.heroBand}`}>
        <div className="mesh-gradient-hero" aria-hidden />
        <div className="relative z-10 flex flex-col gap-4 p-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className={themeClasses.appKit.sectionEyebrow}>{t('overviewTitle')}</p>
            <h2 className="mt-1 text-2xl font-semibold tracking-tight text-text-primary">
              {t('dashboardTitle')}
            </h2>
            <p className="mt-1 text-sm text-text-secondary">
              {t('overviewSubtitle', { date: overviewDate })}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Button type="button" size="sm" onClick={() => navigate('/my-companies')}>
              <Plus className="h-4 w-4" />
              {t('addCompany')}
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 lg:gap-6">
        {kpiCards.map((kpi) => (
          <KpiCard
            key={kpi.nameKey}
            title={t(kpi.nameKey)}
            value={typeof kpi.value === 'number' ? kpi.value.toLocaleString() : kpi.value}
            icon={kpi.icon}
            iconColor={kpi.iconColor}
            iconBgColor={kpi.bgColor}
            subtitle={kpi.subtitleKey ? t(kpi.subtitleKey, kpi.subtitleParams || {}) : undefined}
            trend={kpi.trend && kpi.trendKey && !kpi.subtitleKey}
            trendLabel={kpi.trendKey ? t(kpi.trendKey, kpi.trendParams || {}) : undefined}
            trendPositive={kpi.trendPositive}
          />
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:items-stretch">
        <div className="flex flex-col lg:col-span-2">
          <Card className="flex h-[400px] flex-col p-6">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold tracking-tight text-text-primary">
                {t('globalRiskDistribution')}
              </h3>
              <button
                type="button"
                onClick={() => setMapRefreshTrigger((n) => n + 1)}
                className="rounded-sm p-2 text-text-secondary transition-colors hover:bg-canvas-soft-2 hover:text-text-primary"
                aria-label={t('refreshData')}
              >
                <RefreshCw className="h-4 w-4" />
              </button>
            </div>
            <div className="relative min-h-0 flex-1 overflow-hidden rounded-md border border-border-light bg-canvas-soft">
              <InteractiveWorldMap
                watchlistItems={watchlist}
                riskAnalyses={mapRiskAnalyses}
                compact
                refreshTrigger={mapRefreshTrigger}
                onLoadingComplete={() => setMapLoadingDone(true)}
              />
              {!mapLoadingDone && (
                <div className="absolute inset-0 z-10 overflow-hidden rounded-md">
                  <MapShimmer className="h-full w-full" style={{ minHeight: 320 }} />
                </div>
              )}
            </div>
          </Card>
        </div>
        <div className="flex h-[400px] flex-col">
          <Suspense
            fallback={
              <Card className="h-[400px] p-6">
                <SectionShimmer height={400} />
              </Card>
            }
          >
            <DashboardWatchlistNewsSentiment />
          </Suspense>
        </div>
      </div>

      <Suspense
        fallback={
          <Card className="p-6">
            <SectionShimmer height={320} />
          </Card>
        }
      >
        <DashboardRecentAndAlerts />
      </Suspense>
    </div>
  );
};

export default Dashboard;
